import { useEffect, useState } from 'react'
import { NavigateFunction } from 'react-router-dom'

import { confirmedIcon, printerIco, warningIcon } from '@/assets/icons'
import printVoucherImage from '@/assets/images/printVoucher.jpg'
import vcashLogo from '@/assets/images/vcash.png'
import PrimaryButton from '@/components/atoms/primaryButton/primaryButton'
import WireButton from '@/components/atoms/wireButton/wireButton'
import { useNavigationContext } from '@/hooks'
import { useTranslate } from '@/i18n/useTranslate'

type PaymentCompleteMode = 'processing' | 'printing'
type PrintingVariant = 'voucher' | 'confirmation'

export type PaymentCompleteProps = {
    mode?: PaymentCompleteMode
    printingVariant?: PrintingVariant
    navigate?: NavigateFunction
    onPrimaryButtonClick?: () => void
    hasPrintingError?: boolean
    isPrintCompleted?: boolean
}

export default function PaymentComplete({
    mode = 'processing',
    printingVariant = 'voucher',
    navigate,
    onPrimaryButtonClick,
    hasPrintingError = false,
    isPrintCompleted = false
}: PaymentCompleteProps) {
    const { t } = useTranslate()
    const { startUrl } = useNavigationContext()
    const [showHelpMessage, setShowHelpMessage] = useState<boolean>(false)

    useEffect(() => {
        if (mode !== 'printing' || isPrintCompleted) {
            return
        }
        const timeout = window.setTimeout(() => {
            setShowHelpMessage(true)
        }, 15000)
        return () => clearTimeout(timeout)
    }, [mode, isPrintCompleted])

    if (mode === 'processing') {
        return (
            <div className="payment-successful payment-successful--processing">
                <div className="payment-successful__processing-shell">
                    <img
                        className="payment-successful__processing-logo"
                        src={vcashLogo}
                        alt="VCash logo"
                    />
                    <h1>{t('paymentSuccessful.preparingPayment')}</h1>
                    <div
                        className="payment-successful__loading-pill"
                        aria-hidden="true">
                        <span />
                    </div>
                </div>
            </div>
        )
    }

    const isConfirmationVariant = printingVariant === 'confirmation'
    const titleKey = isConfirmationVariant
        ? 'paymentSuccessful.confirmationTitle'
        : 'paymentSuccessful.title'
    const subtitleKey = isConfirmationVariant
        ? 'paymentSuccessful.confirmationSubtitle'
        : 'paymentSuccessful.subtitle'
    const fallbackTitleKey = isConfirmationVariant
        ? 'paymentSuccessful.confirmationFallbackTitle'
        : 'paymentSuccessful.fallbackTitle'
    const fallbackDescriptionKey = isConfirmationVariant
        ? 'paymentSuccessful.confirmationUnavailable'
        : 'paymentSuccessful.voucherUnavailable'
    const fallbackButtonKey = isConfirmationVariant
        ? 'paymentSuccessful.confirmationButtonText'
        : 'paymentSuccessful.buttonText'
    const warningMessageKey = isConfirmationVariant
        ? 'paymentSuccessful.confirmationPrintWarning'
        : 'paymentSuccessful.voucherPrintWarning'
    const successMessageKey = isConfirmationVariant
        ? 'paymentSuccessful.confirmationSuccessfulMsg'
        : 'voucherGenerated.successfulMsg'
    const inProgressMessageKey = isConfirmationVariant
        ? 'paymentSuccessful.confirmationInProgressMsg'
        : 'voucherGenerated.inProgressMsg'

    const renderError = () => (
        <div className="fallback">
            <div>
                <p>{t(fallbackTitleKey)}</p>
                <p>{t(fallbackDescriptionKey)}</p>
            </div>
            <WireButton onClick={onPrimaryButtonClick}>
                {t(fallbackButtonKey)}
            </WireButton>
        </div>
    )

    const renderSuccess = () => (
        <>
            <div className="successful-msg">
                <img src={confirmedIcon} alt="Confirm icon" />
                {t(successMessageKey)}
            </div>
            <PrimaryButton
                callback={() => {
                    navigate?.(startUrl ?? '/welcome')
                }}
                text={t('voucherGenerated.btnFinish')}
            />
        </>
    )

    const renderInProgress = () => (
        <>
            <div className="warning-message-wrapper">
                <div className="warning-message">
                    <img src={warningIcon} alt="Warning icon" />
                        <div>{t(warningMessageKey)}</div>
                </div>
            </div>
            <div className="successful-msg marginOn">
                <img src={printerIco} alt="Printer icon" />
                {t(inProgressMessageKey)}
            </div>
        </>
    )

    const renderStatus = () => {
        if (!hasPrintingError && isPrintCompleted) {
            return renderSuccess()
        }
        if (hasPrintingError || showHelpMessage) {
            return renderError()
        }
        return renderInProgress()
    }

    return (
        <div className="payment-successful">
            <h1>{t(titleKey)}</h1>
            <h2>{t(subtitleKey)}</h2>

            <div className="demo-wrapper">
                <img src={printVoucherImage} alt={t('insertCash.altText')} />
            </div>

            <div className="fallback-wrapper">{renderStatus()}</div>
        </div>
    )
}
